/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import Icon from "@mui/material/Icon";
import ConfiguratorRoot from "../../examples/Configurator/ConfiguratorRoot";
import Divider from "@mui/material/Divider";
import UpdateUser from "./updateUser";
import NewUser from "./newUser";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import PermissionControl from "components/Utils/PermissionControl";

function Users() {
  const {t} = useTranslation();
  const { userTablecolumns } = data();
  const [users, setUsers] = useState([]);
  const [rawUsers, setRawUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");
  const [filterFirstName, setFilterFirstName] = useState("");
  const [filterLastName, setFilterLastName] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterMobileNo, setFilterMobileNo] = useState("");
  const [filterEmailId, setFilterEmailId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [openConfigurator, setOpenConfigurator] = useState(false);
  const [openNewUserConfigurator, setOpenNewUserConfigurator] = useState(false);
  const [updateUser, setUpdateUser] = useState({});
  const [disableUser, setDisableUser] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const api = useApi();

  const updateUserHandler = (user) => {
    setUpdateUser(user);
    setOpenConfigurator(true);
  };

  const handleDisableItem = (user) => {
    const isCurrentlyActive = user.status === "1" || user.status === 1;
    const action = isCurrentlyActive ? "disabled" : "enabled";
    const icon = isCurrentlyActive ? '🔒' : '✅';
    
    api.post(`/users/status/${user.userId}/toggle`)
    .then((response) => {
      // console.log(response);
      if(response.data.statusCodeValue === 200) {
        toast.success(`User ${action} successfully!`, {
          icon: icon,
          duration: 3000,
        });
        setOpenConfirmDialog(false);
        getUsers();
      }
    })
    .catch((error) => {
      toast.error(`Failed to ${isCurrentlyActive ? 'disable' : 'enable'} user`, { duration: 3000 });
      console.error(error);
    });
  };

  const handleConfirmDisable = (user) => {
    setDisableUser(user);
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
    setDisableUser({});
  };
  const getUsers = () => {
    api.get("users/browse")
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setRawUsers(response.data.body);
        } else {
          toast.error(response.data.statusCode);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        console.log(error.response);
      })
  }

  useEffect(() => {
    if(openConfigurator === false && openNewUserConfigurator === false) {
      getUsers();
    }
  },[openConfigurator, openNewUserConfigurator]);

  useEffect(() => {
      getUsers();
  },[]);

  useEffect(() => {
    let result = [...rawUsers];
    if (filterUserId.trim()) result = result.filter(u => u.userId?.toLowerCase().includes(filterUserId.trim().toLowerCase()));
    if (filterFirstName.trim()) result = result.filter(u => u.firstName?.toLowerCase().includes(filterFirstName.trim().toLowerCase()));
    if (filterLastName.trim()) result = result.filter(u => u.lastName?.toLowerCase().includes(filterLastName.trim().toLowerCase()));
    if (filterRole) result = result.filter(u => u.role === filterRole);
    if (filterMobileNo.trim()) result = result.filter(u => u.mobileNo?.includes(filterMobileNo.trim()));
    if (filterEmailId.trim()) result = result.filter(u => u.emailId?.toLowerCase().includes(filterEmailId.trim().toLowerCase()));
    if (filterStatus) result = result.filter(u => String(u.status) === filterStatus);
    const modifiedData = result.map(item => {
      const isActive = item.status === "1" || item.status === 1;
      const action = (
        <MDBox display="flex" alignItems="center" gap={1}>
          <PermissionControl permission={{ restrict: true, module: "User", subModule: "ToggleAdminUserStatus" }}>
            <MDButton variant="contained" size="small" color={isActive ? "error" : "success"} onClick={() => handleConfirmDisable(item)}>
              {isActive ? "Disable" : "Enable"}
            </MDButton>
          </PermissionControl>
          <MDButton variant="contained" size="small" color="info" onClick={() => updateUserHandler(item)}>
            Edit
          </MDButton>
        </MDBox>
      );
      const statusChip = (
        <Chip label={isActive ? "Active" : "Inactive"} variant="outlined" color={isActive ? "success" : "error"} />
      );
      const displayRole = item.role === "MAKER" ? "ADMIN" : item.role;
      return { ...item, action, status: statusChip, role: displayRole };
    });
    setUsers(modifiedData);
  }, [rawUsers, filterUserId, filterFirstName, filterLastName, filterRole, filterMobileNo, filterEmailId, filterStatus]);

  const resetFilters = () => {
    setFilterUserId("");
    setFilterFirstName("");
    setFilterLastName("");
    setFilterRole("");
    setFilterMobileNo("");
    setFilterEmailId("");
    setFilterStatus("");
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  Users
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="secondary">
                  {users.length} Users
                </MDTypography>
              </MDBox>
              <MDBox display="flex">
                <MDButton color="info" variant="gradient" onClick={() => setOpenNewUserConfigurator(true)}>New User</MDButton>
              </MDBox>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="User ID"
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="First Name"
                    value={filterFirstName}
                    onChange={(e) => setFilterFirstName(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Last Name"
                    value={filterLastName}
                    onChange={(e) => setFilterLastName(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Mobile No"
                    value={filterMobileNo}
                    onChange={(e) => setFilterMobileNo(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Email"
                    value={filterEmailId}
                    onChange={(e) => setFilterEmailId(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Role"
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="MAKER" style={{ fontSize: "0.8rem" }}>Admin</MenuItem>
                    <MenuItem value="REPORTING_ADMIN" style={{ fontSize: "0.8rem" }}>Reporting Admin</MenuItem>
                    <MenuItem value="SETTLEMENT_ADMIN" style={{ fontSize: "0.8rem" }}>Settlement Admin</MenuItem>
                    <MenuItem value="ONBOARDING_ADMIN" style={{ fontSize: "0.8rem" }}>Onboarding Admin</MenuItem>
                    <MenuItem value="ONBOARDING_REPORTING_ADMIN" style={{ fontSize: "0.8rem" }}>Onboarding &amp; Reporting Admin</MenuItem>
                    <MenuItem value="G_MAKER" style={{ fontSize: "0.8rem" }}>Guest Admin</MenuItem>
                    <MenuItem value="SUPERADMIN" style={{ fontSize: "0.8rem" }}>Super Admin</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="1" style={{ fontSize: "0.8rem" }}>Active</MenuItem>
                    <MenuItem value="0" style={{ fontSize: "0.8rem" }}>Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs="auto">
                  <MDButton variant="outlined" color="secondary" size="small" onClick={resetFilters}
                    sx={{ height: "38px", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    Reset
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <DataTable
              table={{ columns : userTablecolumns, rows : users }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              entriesPerPagePosition="bottom"
              canSearch={false}
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <UpdateUser user={updateUser} openConfigurator={openConfigurator} setOpenConfigurator={setOpenConfigurator} />
      <NewUser openConfigurator={openNewUserConfigurator} setOpenConfigurator={setOpenNewUserConfigurator} />
      
      <Dialog open={openConfirmDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            Do you want to {disableUser.status === "1" || disableUser.status === 1 ? "disable" : "enable"} this user?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseDialog}>
            Cancel
          </MDButton>
          <MDButton
            color={disableUser.status === "1" || disableUser.status === 1 ? "error" : "success"}
            variant="contained"
            onClick={() => handleDisableItem(disableUser)}
          >
            {disableUser.status === "1" || disableUser.status === 1 ? "Disable" : "Enable"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Users;
